import { z } from '@customerio/hydra-validation';
import type { DsGlobalStylesReferenceItem } from '@customerio/hydra-ui-api';
import type { GlobalStyles } from '@internal/global-styles-utils';
import { isRecord } from '@/utils/objects';
import { defineEntity, readSchema } from '../shared/define-entity';
import { type ReferenceDraft, toReferenceDraft } from '../ds-node/draft-reference';

/**
 * Design Studio global styles entity — workspace-scoped singleton.
 *
 * A workspace has exactly one global styles object (the design-token tree the
 * visual editor themes emails against). The ui_api GET/PUT carry no id beyond
 * `environment_id`, so this is a singleton: `pk` is a constant and the object
 * lives in a single detail slot per workspace (`dsGlobalStylesKeys.current`).
 *
 * **Opaque body.** The GET response is typed as `{ [key: string]: unknown }`
 * in the ui_api spec — the token tree itself is not modeled — so `DsGlobalStyles`
 * is an opaque record and the read schema is a pass-through (no date/BigNumber
 * transforms to declare). Parcel treats it the same way (`useConnectGlobalStyles`
 * stores the raw `fly` GET body as `globalStyles`).
 *
 * **No write schema.** The PUT body wraps the styles object
 * (`{ global_styles, variant_replacements }`) and returns 204, so nothing
 * round-trips through the entity type — mutations build the body by hand from
 * the generated request type (see `mutations.ts`).
 *
 * The extract / process / status reads and mutations are auxiliary (AI style
 * generation); they return the generated wire types directly, mirroring how
 * `ds-node` treats `search` / `parentEntities`. The references "used in" read is
 * the one exception: it camelCases in-app rows and normalizes their Unix-seconds
 * timestamps to ISO so the restored in-app publish path consumes the same
 * discriminated union parcel did (see `transformGlobalStylesReferences` below).
 */

/* ------------------------------------------------------------------------- */
/* Wire type re-exports — the generated ui_api models                         */
/* ------------------------------------------------------------------------- */

export type {
  DsGlobalStylesProcessSource,
  DsGlobalStylesProcessSourceStatus,
  DsGlobalStylesProcessStatusResponse,
  DsGlobalStylesProcessStylesRequest,
  DsGlobalStylesProcessStylesResponse,
  DsGlobalStylesProcessUploadRequest,
  DsGlobalStylesProcessUploadResponse,
  DsGlobalStylesReferenceItem,
  DsGlobalStylesUpdateGlobalStylesRequest,
  DsGlobalStylesVariantReplacement,
} from '@customerio/hydra-ui-api';

/** The AI-extracted styles payload returned by the extract/process status polls. */
export type { ServicesAiExtractedGlobalStyles as DsExtractedGlobalStyles } from '@customerio/hydra-ui-api';

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

/**
 * The workspace global styles object (design-token tree). The ui_api spec
 * models the GET body as `{ [key: string]: unknown }`; the shape it carries is
 * parcel's `GlobalStyles` token tree, which the design-studio feature consumes
 * directly, so the entity is typed as that model rather than an opaque record.
 */
export type DsGlobalStyles = GlobalStyles;

/* ------------------------------------------------------------------------- */
/* Schema — pass-through (no transforms)                                       */
/* ------------------------------------------------------------------------- */

const DsGlobalStylesFromApi = readSchema<DsGlobalStyles>(z.looseObject({}));

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** Constant pk — one global styles object per workspace. */
const SINGLETON_ID = 'current';

/** @public */
export const dsGlobalStyles = defineEntity<DsGlobalStyles, never, never, undefined, 'workspace'>({
  name: 'ds-global-styles',
  scope: 'workspace',
  pk: () => SINGLETON_ID,
  schema: { read: DsGlobalStylesFromApi },
});

export const dsGlobalStylesKeys = {
  ...dsGlobalStyles.keys,
  /** The workspace's global styles object (singleton detail slot). */
  current: (workspaceId: number) => dsGlobalStyles.keys.detail(workspaceId, SINGLETON_ID),
  /** Nodes that reference the workspace global styles ("used in" list). */
  references: (workspaceId: number, includeInApp: boolean) =>
    [...dsGlobalStyles.keys.all(workspaceId), 'references', includeInApp] as const,
  /** Multi-source AI extraction status poll. */
  processStatus: (workspaceId: number, workflowId: string, runId: string) =>
    [...dsGlobalStyles.keys.all(workspaceId), 'process-status', workflowId, runId] as const,
};

/**
 * Unwrap the optional `{ styles, brand }` envelope from the GET body.
 *
 * The brand × global-styles merge makes `GET /ds/global_styles` return the
 * token tree wrapped as `{ styles, brand }` when `improve-ds-agent` is on, so
 * the Styles page gets the brand document in the same fetch. Flag off, the
 * endpoint still returns the bare token tree.
 *
 * Detect the envelope by the presence of *either* key and unwrap to the token
 * tree, coalescing an absent or null `styles` to `null` (the graceful "no styles
 * yet"). Keying on `brand` too — not just `styles` — is what makes a styles-less
 * envelope safe: the backend can drop an empty `styles` (Go `omitempty`) and
 * send `{ brand }` alone, which must still resolve to `null` rather than being
 * mistaken for the token tree. Otherwise pass the body through unchanged, which
 * keeps every existing token-tree consumer working across both shapes. The
 * envelope's `brand` field is discarded at this boundary — the brand surfaces
 * fetch the document from the separate `/ds/brand_document` query today.
 *
 * (Heuristic: a workspace can't currently name a theme `styles` or `brand`, so
 * either top-level key unambiguously marks the envelope.)
 */
function unwrapStylesEnvelope(raw: unknown): unknown {
  if (isRecord(raw) && ('styles' in raw || 'brand' in raw)) {
    return raw.styles ?? null;
  }
  return raw;
}

/**
 * Parse a GET body into the entity type, tolerating a literal `null` and the
 * optional `{ styles, brand }` envelope.
 *
 * The ui_api can marshal an unset styles object as JSON `null` (a fresh
 * workspace that has never saved styles); treat `null` as a clean "no styles
 * yet" success and pass it straight through instead of running it against the
 * object schema — otherwise the loose-object schema would throw a ZodError and
 * surface as an error state instead of the graceful empty state (CON-7608).
 *
 * When `improve-ds-agent` is on the body is wrapped as `{ styles, brand }`; the
 * token tree is unwrapped here (see {@link unwrapStylesEnvelope}) so the shape
 * flip is invisible to styles consumers. Unwrap *before* the null check so a
 * styles-less workspace — whether serialized as `{ styles: null }` or with
 * `styles` omitted entirely (`{ brand }`) — also resolves to the graceful "no
 * styles yet" null rather than a ZodError.
 */
export function parseDsGlobalStyles(raw: unknown): DsGlobalStyles | null {
  const body = unwrapStylesEnvelope(raw);
  if (body == null) {
    return null;
  }
  return dsGlobalStyles.parse(body);
}

/* ------------------------------------------------------------------------- */
/* References transform — camelCase + in-app timestamp normalization           */
/* ------------------------------------------------------------------------- */

/**
 * Camel-cased shape of a node that references the workspace global styles.
 * Ported from parcel's
 * `app/src/services/api/transformers/global-styles-references.ts` (CON-7595).
 *
 *  - Regular nodes (EMAIL, COMPONENT, …) carry `{ id, type }`.
 *  - Legacy Journeys in-app templates carry the full row, under their own
 *    `LEGACY_IN_APP_TEMPLATE` literal.
 *
 * Only the fields the save flow + bulk-publish modal actually read are mapped
 * (no dead generality): the EMAIL path needs `{ id, type }`; the in-app path
 * needs `name`, `body` (row preview), the four parent-entity link ids (the
 * "Used in" label + external URL) and `updatedAt` (rendered via `new Date(…)`;
 * absent wire timestamps stay `undefined` rather than mapping to epoch 0).
 */
export type GlobalStylesReferenceNode = {
  id: string;
  type: string;
  /** Set for an email in an open Automation draft. */
  draft?: ReferenceDraft;
  /**
   * Never set on a node. Declared so the union stays discriminable: the
   * transform populates `body` only on legacy templates, and TypeScript infers
   * the narrowing from that without an unchecked type predicate. The value's
   * own `type: 'LEGACY_IN_APP_TEMPLATE'` is still the semantic discriminator —
   * read that, not this.
   */
  body?: undefined;
};

/**
 * A Journeys in-app *template*, not a Design Studio node. It has no `parts`,
 * `settings` or `parentId`; it carries a raw `body` and the id of the Journeys
 * entity it belongs to.
 *
 * `type` is `LEGACY_IN_APP_TEMPLATE`, the literal ui_api sends for these rows
 * from INAPP-15102 on. It never reuses `IN_APP`, which is also a real
 * `ds_nodes` type: sharing it would leave the two indistinguishable downstream,
 * and a caller keying on `type === 'IN_APP'` would mis-sort a genuine in-app
 * node into this branch with no error to show for it (INAPP-14810).
 */
export interface LegacyInAppTemplateReference {
  id: string;
  type: 'LEGACY_IN_APP_TEMPLATE';
  name: string;
  body: string;
  actionId?: number;
  newsletterId?: number;
  transactionalMessageId?: number;
  inAppBroadcastId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export type GlobalStylesReference = GlobalStylesReferenceNode | LegacyInAppTemplateReference;

/**
 * Transforms one raw ui_api reference row into the camelCased frontend shape.
 * Legacy in-app rows convert the Unix-seconds `created_at`/`updated_at` into
 * ISO strings so call sites can use `new Date(node.updatedAt)` directly (parcel
 * parity). EMAIL/other rows pass through as `{ id, type }`.
 *
 * This is where the wire response is split, and the only place the split
 * belongs; `isLegacyInAppTemplateRow` decides it.
 */
export function transformGlobalStylesReference(item: DsGlobalStylesReferenceItem): GlobalStylesReference {
  if (!isLegacyInAppTemplateRow(item)) {
    const draft = toReferenceDraft(item);
    return draft == null ? { id: item.id, type: item.type } : { id: item.id, type: item.type, draft };
  }

  const result: LegacyInAppTemplateReference = {
    id: item.id,
    type: 'LEGACY_IN_APP_TEMPLATE',
    name: item.name ?? '',
    body: item.body ?? '',
  };
  if (item.created_at != null) {
    result.createdAt = unixSecondsToIso(item.created_at);
  }
  if (item.updated_at != null) {
    result.updatedAt = unixSecondsToIso(item.updated_at);
  }
  if (item.action_id != null) {
    result.actionId = item.action_id;
  }
  if (item.newsletter_id != null) {
    result.newsletterId = item.newsletter_id;
  }
  if (item.transactional_message_id != null) {
    result.transactionalMessageId = item.transactional_message_id;
  }
  if (item.in_app_broadcast_id != null) {
    result.inAppBroadcastId = item.in_app_broadcast_id;
  }
  return result;
}

/**
 * Whether a references row is a Journeys in-app template rather than a node.
 *
 * From INAPP-15102 on, ui_api types a template row `LEGACY_IN_APP_TEMPLATE`,
 * and that type decides on its own, so a missing or null `body` reads as an
 * empty one.
 *
 * Before it, a template arrives as `type: 'IN_APP'`, which a real in-app node
 * from node search carries too, so that shape needs the type and a present,
 * non-null `body` together. The generated type declares `body?: string | null`
 * on every row, and a node row serializing `body: null` stays a node. The Go
 * endpoint sets the body on every template row: `referenceItem.Body` is a
 * `*string`, non-nil on template rows so the key survives an empty body, and
 * nil on node rows so `omitempty` drops it
 * (`services/ui_api/ds_global_styles/references.go`). An empty string is a real
 * body, which is why this tests for null rather than truthiness. INAPP-15103
 * removes this shape once INAPP-15102 is deployed in both regions.
 */
function isLegacyInAppTemplateRow(item: DsGlobalStylesReferenceItem): boolean {
  if (item.type === 'LEGACY_IN_APP_TEMPLATE') {
    return true;
  }
  return item.type === 'IN_APP' && item.body != null;
}

/** Transforms the full ui_api references array into its camelCased form. */
export function transformGlobalStylesReferences(items: DsGlobalStylesReferenceItem[]): GlobalStylesReference[] {
  return items.map(transformGlobalStylesReference);
}

function unixSecondsToIso(seconds: number): string {
  return new Date(seconds * 1000).toISOString();
}
